const JOURNAL_SCHEMA =
  'restack.locker.office-quiescence-journal.v2';
const DEFAULT_STORAGE_KEY =
  'restack.locker.office-quiescence-journal';
const MAX_ENTRIES = 128;
const IDENTIFIER = /^[^/\u0000-\u001F\u007F]{1,512}$/;
const OPERATION_ID = /^[0-9a-f]{32}$/;
const RECEIPT = /^[0-9a-f]{64}$/;
const JOURNAL_STATUSES = Object.freeze([
  'prepared',
  'client_persisted',
  'running',
  'result_delivered',
  'settled',
]);
const STATUS_INDEX = Object.freeze(
  Object.fromEntries(
    JOURNAL_STATUSES.map((status, index) => [status, index])
  )
);
const RECOVERY_CLASSIFICATIONS = Object.freeze({
  SAFE_TO_ABANDON: 'safe_to_abandon',
  REQUIRES_EXPLICIT_RESOLUTION: 'requires_explicit_resolution',
  POLL_EXACT_COMPLETION: 'poll_exact_completion',
});
const RECOVERY_BY_STATUS = Object.freeze({
  prepared: RECOVERY_CLASSIFICATIONS.SAFE_TO_ABANDON,
  client_persisted: RECOVERY_CLASSIFICATIONS.SAFE_TO_ABANDON,
  running:
    RECOVERY_CLASSIFICATIONS.REQUIRES_EXPLICIT_RESOLUTION,
  result_delivered:
    RECOVERY_CLASSIFICATIONS.POLL_EXACT_COMPLETION,
  settled:
    RECOVERY_CLASSIFICATIONS.POLL_EXACT_COMPLETION,
});

/**
 * What the server said the last time this entry was reconciled.
 *
 * UNKNOWN_TO_SERVER is terminal: the fence holds no pending record for this
 * receipt, so no server-side write can still be in flight and the entry is
 * dropped. SERVER_CONFIRMED_PENDING is the fence doing its job and must never
 * be dropped without the server's own resolution path. UNRECONCILED covers
 * every answer that proves nothing (transport failure, 5xx, an unreadable
 * fence, a receipt the server rejects); it is retried, but only up to a
 * ceiling, because retrying it forever is what wedged a pane in production.
 */
const RECONCILE_OUTCOMES = Object.freeze({
  UNKNOWN_TO_SERVER: 'unknown_to_server',
  SERVER_CONFIRMED_PENDING: 'server_confirmed_pending',
  UNRECONCILED: 'unreconciled',
});
const RECONCILE_OUTCOME_VALUES = Object.freeze(
  Object.values(RECONCILE_OUTCOMES)
);
// Six attempts at the caller's 15 second cadence is about 90 seconds of
// retrying before the client stops asking and hands the user an explicit
// choice instead.
const RECONCILE_ATTEMPT_CEILING = 6;
// An entry the server has never confirmed as pending is retired once it is
// this old, so a journal entry can never outlive the incident that made it.
const UNBACKED_ENTRY_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_RECONCILE_ATTEMPTS_STORED = 1_000_000;

export class LockerOfficeQuiescenceJournalError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'LockerOfficeQuiescenceJournalError';
    this.code = code;
  }
}

function journalError(code, message) {
  return new LockerOfficeQuiescenceJournalError(code, message);
}

function requireIdentifier(value, field) {
  const normalized = typeof value === 'string' ? value.trim() : '';
  if (!IDENTIFIER.test(normalized)) {
    throw journalError(
      'locker_office_journal_invalid',
      `Invalid ${field}.`
    );
  }
  return normalized;
}

function requireOperationId(value) {
  const normalized = typeof value === 'string'
    ? value.toLowerCase()
    : '';
  if (!OPERATION_ID.test(normalized)) {
    throw journalError(
      'locker_office_journal_invalid',
      'Invalid Office operation identifier.'
    );
  }
  return normalized;
}

function requireReceipt(value) {
  const normalized = typeof value === 'string'
    ? value.toLowerCase()
    : '';
  if (!RECEIPT.test(normalized)) {
    throw journalError(
      'locker_office_journal_invalid',
      'Invalid Office operation receipt.'
    );
  }
  return normalized;
}

function normalizeDate(value, field) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    throw journalError(
      'locker_office_journal_invalid',
      `Invalid ${field}.`
    );
  }
  return date.toISOString();
}

function optionalDate(value, required, field) {
  if (!required) {
    if (value !== null && value !== undefined) {
      throw journalError(
        'locker_office_journal_corrupt',
        'Secure Office operation recovery data is invalid.'
      );
    }
    return null;
  }
  return normalizeDate(value, field);
}

function optionalReconcileCount(value) {
  if (value === null || value === undefined) return 0;
  if (
    !Number.isInteger(value)
    || value < 0
    || value > MAX_RECONCILE_ATTEMPTS_STORED
  ) {
    throw journalError(
      'locker_office_journal_corrupt',
      'Secure Office operation recovery data is invalid.'
    );
  }
  return value;
}

function optionalFlag(value) {
  if (value === null || value === undefined) return false;
  if (typeof value !== 'boolean') {
    throw journalError(
      'locker_office_journal_corrupt',
      'Secure Office operation recovery data is invalid.'
    );
  }
  return value;
}

function assertTimestampOrder(entry) {
  const timestamps = [
    entry.preparedAt,
    entry.clientPersistedAt,
    entry.runningAt,
    entry.resultDeliveredAt,
    entry.settledAt,
  ].filter(Boolean);
  for (let index = 1; index < timestamps.length; index += 1) {
    if (timestamps[index] < timestamps[index - 1]) {
      throw journalError(
        'locker_office_journal_corrupt',
        'Secure Office operation recovery data is invalid.'
      );
    }
  }
}

function normalizeEntry(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw journalError(
      'locker_office_journal_corrupt',
      'Secure Office operation recovery data is invalid.'
    );
  }
  const operationId = requireOperationId(value.operationId);
  const status = value.status;
  const statusIndex = STATUS_INDEX[status];
  if (!Number.isInteger(statusIndex)) {
    throw journalError(
      'locker_office_journal_corrupt',
      'Secure Office operation recovery data is invalid.'
    );
  }
  const entry = Object.freeze({
    operationId,
    receipt: requireReceipt(value.receipt),
    userId: requireIdentifier(value.userId, 'userId'),
    chatId: requireIdentifier(value.chatId, 'chatId'),
    callId: requireIdentifier(value.callId, 'callId'),
    status,
    preparedAt: normalizeDate(value.preparedAt, 'preparedAt'),
    clientPersistedAt: optionalDate(
      value.clientPersistedAt,
      statusIndex >= STATUS_INDEX.client_persisted,
      'clientPersistedAt'
    ),
    runningAt: optionalDate(
      value.runningAt,
      statusIndex >= STATUS_INDEX.running,
      'runningAt'
    ),
    resultDeliveredAt: optionalDate(
      value.resultDeliveredAt,
      statusIndex >= STATUS_INDEX.result_delivered,
      'resultDeliveredAt'
    ),
    settledAt: optionalDate(
      value.settledAt,
      statusIndex >= STATUS_INDEX.settled,
      'settledAt'
    ),
    // Reconciliation bookkeeping is deliberately outside the lifecycle chain
    // above: it records what the server answered, not what Office did. An
    // entry written before this field existed reads back as never reconciled.
    reconcileAttempts: optionalReconcileCount(
      value.reconcileAttempts
    ),
    lastReconcileAt: value.lastReconcileAt === null
      || value.lastReconcileAt === undefined
      ? null
      : normalizeDate(value.lastReconcileAt, 'lastReconcileAt'),
    serverConfirmedPending: optionalFlag(
      value.serverConfirmedPending
    ),
  });
  assertTimestampOrder(entry);
  return entry;
}

function lastLifecycleAt(entry) {
  return entry.settledAt
    || entry.resultDeliveredAt
    || entry.runningAt
    || entry.clientPersistedAt
    || entry.preparedAt;
}

function parseStoredEntry(serialized, operationId) {
  if (!serialized) return null;
  let value;
  try {
    value = JSON.parse(serialized);
  } catch {
    throw journalError(
      'locker_office_journal_corrupt',
      'Secure Office operation recovery data is invalid.'
    );
  }
  if (
    !value
    || typeof value !== 'object'
    || Array.isArray(value)
    || value.schema !== JOURNAL_SCHEMA
    || !value.entry
  ) {
    throw journalError(
      'locker_office_journal_corrupt',
      'Secure Office operation recovery data is invalid.'
    );
  }
  const entry = normalizeEntry(value.entry);
  if (entry.operationId !== operationId) {
    throw journalError(
      'locker_office_journal_corrupt',
      'Secure Office operation recovery data is invalid.'
    );
  }
  return entry;
}

function cloneEntry(entry) {
  return Object.freeze({ ...entry });
}

/**
 * Accept either a plain boolean (legacy callers) or a classified answer.
 * Removal is tied to one outcome only: the server holding no pending record
 * for this receipt. Any other shape, including a malformed one, keeps the
 * entry, so a confused answer can never delete the record of a live write.
 */
function normalizeConfirmation(answer) {
  if (answer === true) {
    return {
      completed: true,
      outcome: RECONCILE_OUTCOMES.UNKNOWN_TO_SERVER,
    };
  }
  const outcome = RECONCILE_OUTCOME_VALUES.includes(answer?.outcome)
    ? answer.outcome
    : RECONCILE_OUTCOMES.UNRECONCILED;
  return {
    completed: outcome === RECONCILE_OUTCOMES.UNKNOWN_TO_SERVER,
    outcome,
  };
}

function sameIdentity(left, right) {
  return (
    left.operationId === right.operationId
    && left.receipt === right.receipt
    && left.userId === right.userId
    && left.chatId === right.chatId
    && left.callId === right.callId
  );
}

/**
 * True once the client has asked the server about an entry enough times
 * without ever being told the operation is genuinely pending. The caller must
 * stop polling at this point: the answers so far prove nothing, and repeating
 * them at a fixed cadence is an infinite loop, not a recovery strategy.
 */
export function isReconcileExhausted(
  entry,
  ceiling = RECONCILE_ATTEMPT_CEILING
) {
  return (
    entry?.serverConfirmedPending !== true
    && Number(entry?.reconcileAttempts || 0) >= ceiling
  );
}

/**
 * Persist server-issued operation receipts before Office dispatch. Recovery
 * may abandon prepared or client_persisted operations. A running operation
 * requires explicit resolution. A result-delivered operation first polls the
 * exact read-only completion endpoint. If it remains pending, the user may
 * explicitly resolve that one receipt after reviewing the open file.
 *
 * Every reconciliation answer is recorded on the entry. An entry the server
 * never confirms as pending is bounded twice over: polling stops at the
 * attempt ceiling, and the entry is retired outright once it passes the age
 * ceiling. An entry the server DOES confirm as pending is never retired here,
 * because that is a real in-flight write to the user's open file and only the
 * server's own resolution path may clear it.
 */
export class LockerOfficeQuiescenceJournal {
  constructor({
    storage,
    storageKey = DEFAULT_STORAGE_KEY,
    now = () => new Date(),
  }) {
    if (
      !storage
      || typeof storage.getItem !== 'function'
      || typeof storage.setItem !== 'function'
      || typeof storage.removeItem !== 'function'
      || typeof storage.key !== 'function'
      || !Number.isFinite(Number(storage.length))
    ) {
      throw journalError(
        'locker_office_journal_unavailable',
        'Secure Office operation recovery storage is unavailable.'
      );
    }
    this.storage = storage;
    this.storageKey = storageKey;
    this.now = now;
  }

  _entryKey(operationId) {
    return `${this.storageKey}:${requireOperationId(operationId)}`;
  }

  _readEntry(operationId) {
    const oid = requireOperationId(operationId);
    try {
      return parseStoredEntry(
        this.storage.getItem(this._entryKey(oid)),
        oid
      );
    } catch (error) {
      if (error instanceof LockerOfficeQuiescenceJournalError) {
        throw error;
      }
      throw journalError(
        'locker_office_journal_unavailable',
        'Secure Office operation recovery storage is unavailable.'
      );
    }
  }

  _writeEntry(entry) {
    try {
      this.storage.setItem(
        this._entryKey(entry.operationId),
        JSON.stringify({
          schema: JOURNAL_SCHEMA,
          entry,
        })
      );
    } catch {
      throw journalError(
        'locker_office_journal_unavailable',
        'Secure Office operation recovery storage is unavailable.'
      );
    }
  }

  _removeEntry(operationId) {
    try {
      this.storage.removeItem(this._entryKey(operationId));
    } catch {
      throw journalError(
        'locker_office_journal_unavailable',
        'Secure Office operation recovery storage is unavailable.'
      );
    }
  }

  _entryKeys() {
    try {
      const prefix = `${this.storageKey}:`;
      const keys = [];
      for (let index = 0; index < this.storage.length; index += 1) {
        const key = this.storage.key(index);
        if (typeof key === 'string' && key.startsWith(prefix)) {
          keys.push(key);
        }
      }
      if (keys.length > MAX_ENTRIES) {
        throw journalError(
          'locker_office_journal_corrupt',
          'Secure Office operation recovery data is invalid.'
        );
      }
      return keys;
    } catch (error) {
      if (error instanceof LockerOfficeQuiescenceJournalError) {
        throw error;
      }
      throw journalError(
        'locker_office_journal_unavailable',
        'Secure Office operation recovery storage is unavailable.'
      );
    }
  }

  _readAll() {
    const prefix = `${this.storageKey}:`;
    return this._entryKeys().map((key) => {
      const operationId = key.slice(prefix.length);
      return this._readEntry(operationId);
    });
  }

  _transition(operationId, targetStatus, timestampField) {
    const oid = requireOperationId(operationId);
    const previous = this._readEntry(oid);
    if (!previous) {
      throw journalError(
        'locker_office_journal_missing',
        'Secure Office operation recovery data is missing.'
      );
    }
    const currentIndex = STATUS_INDEX[previous.status];
    const targetIndex = STATUS_INDEX[targetStatus];
    if (currentIndex >= targetIndex) {
      return cloneEntry(previous);
    }
    if (currentIndex !== targetIndex - 1) {
      throw journalError(
        'locker_office_journal_transition',
        'Secure Office operation transition is invalid.'
      );
    }
    const next = normalizeEntry({
      ...previous,
      status: targetStatus,
      [timestampField]: this.now(),
    });
    this._writeEntry(next);
    return cloneEntry(next);
  }

  recordPrepared({
    operationId,
    receipt,
    userId,
    chatId,
    callId,
  }) {
    const oid = requireOperationId(operationId);
    const next = normalizeEntry({
      operationId: oid,
      receipt,
      userId,
      chatId,
      callId,
      status: 'prepared',
      preparedAt: this.now(),
      clientPersistedAt: null,
      runningAt: null,
      resultDeliveredAt: null,
      settledAt: null,
    });
    const previous = this._readEntry(oid);
    if (previous) {
      if (!sameIdentity(previous, next)) {
        throw journalError(
          'locker_office_journal_conflict',
          'Secure Office operation identity changed.'
        );
      }
      return cloneEntry(previous);
    }
    if (this._entryKeys().length >= MAX_ENTRIES) {
      throw journalError(
        'locker_office_journal_capacity',
        'Too many secure Office operations need recovery.'
      );
    }
    this._writeEntry(next);
    return cloneEntry(next);
  }

  markClientPersisted(operationId) {
    return this._transition(
      operationId,
      'client_persisted',
      'clientPersistedAt'
    );
  }

  markRunning(operationId) {
    return this._transition(
      operationId,
      'running',
      'runningAt'
    );
  }

  markSettled(operationId) {
    return this._transition(
      operationId,
      'settled',
      'settledAt'
    );
  }

  markResultDelivered(operationId) {
    return this._transition(
      operationId,
      'result_delivered',
      'resultDeliveredAt'
    );
  }

  abandonSafe(operationId) {
    const oid = requireOperationId(operationId);
    const previous = this._readEntry(oid);
    if (!previous) return false;
    if (
      RECOVERY_BY_STATUS[previous.status]
      !== RECOVERY_CLASSIFICATIONS.SAFE_TO_ABANDON
    ) {
      throw journalError(
        'locker_office_journal_resolution_required',
        'Secure Office operation requires explicit resolution.'
      );
    }
    this._removeEntry(oid);
    return true;
  }

  removeExplicitlyResolved({
    operationId,
    receipt,
    userId,
    chatId,
    callId,
    confirmReviewed,
  } = {}) {
    if (confirmReviewed !== true) {
      throw journalError(
        'locker_office_journal_resolution_required',
        'Review confirmation is required for this Office operation.'
      );
    }
    const oid = requireOperationId(operationId);
    const previous = this._readEntry(oid);
    if (!previous) return false;
    const reviewed = normalizeEntry({
      ...previous,
      operationId,
      receipt,
      userId,
      chatId,
      callId,
    });
    if (!sameIdentity(previous, reviewed)) {
      throw journalError(
        'locker_office_journal_conflict',
        'Secure Office operation identity changed.'
      );
    }
    if (
      previous.status !== 'running'
      && previous.status !== 'result_delivered'
    ) {
      throw journalError(
        'locker_office_journal_resolution_required',
        'Only an unknown running or result-delivered Office operation may be resolved.'
      );
    }
    this._removeEntry(oid);
    return true;
  }

  removeConfirmedComplete({
    operationId,
    receipt,
    userId,
    chatId,
    callId,
  } = {}) {
    const oid = requireOperationId(operationId);
    const previous = this._readEntry(oid);
    if (!previous) return false;
    const confirmed = normalizeEntry({
      ...previous,
      operationId,
      receipt,
      userId,
      chatId,
      callId,
    });
    if (!sameIdentity(previous, confirmed)) {
      throw journalError(
        'locker_office_journal_conflict',
        'Secure Office operation identity changed.'
      );
    }
    if (
      RECOVERY_BY_STATUS[previous.status]
      !== RECOVERY_CLASSIFICATIONS.POLL_EXACT_COMPLETION
    ) {
      throw journalError(
        'locker_office_journal_resolution_required',
        'The Office operation is not ready for completion cleanup.'
      );
    }
    this._removeEntry(oid);
    return true;
  }

  /**
   * Record what the server answered for one entry. UNKNOWN_TO_SERVER is not
   * recorded here: the caller removes that entry instead, because an operation
   * the server has no record of cannot be pending.
   */
  recordReconcileOutcome(operationId, outcome) {
    if (!RECONCILE_OUTCOME_VALUES.includes(outcome)) {
      throw journalError(
        'locker_office_journal_invalid',
        'Invalid Office operation reconciliation outcome.'
      );
    }
    const oid = requireOperationId(operationId);
    const previous = this._readEntry(oid);
    if (!previous) return null;
    const next = normalizeEntry({
      ...previous,
      reconcileAttempts: Math.min(
        previous.reconcileAttempts + 1,
        MAX_RECONCILE_ATTEMPTS_STORED
      ),
      lastReconcileAt: this.now(),
      // Server-confirmed pending is sticky. One 409 that names this exact
      // receipt proves a real operation existed; a later unreadable answer
      // must not downgrade that into something the client may discard.
      serverConfirmedPending:
        previous.serverConfirmedPending
        || outcome === RECONCILE_OUTCOMES.SERVER_CONFIRMED_PENDING,
    });
    this._writeEntry(next);
    return cloneEntry(next);
  }

  /**
   * Drop an entry the server has answered for terminally: it holds no pending
   * record of this exact receipt, so nothing about it can still be in flight.
   * Callers must only reach here from a receipt-scoped server answer, never
   * from a timeout or a transport failure, which prove nothing.
   */
  removeUnknownToServer({
    operationId,
    receipt,
    userId,
    chatId,
    callId,
  } = {}) {
    const oid = requireOperationId(operationId);
    const previous = this._readEntry(oid);
    if (!previous) return false;
    const unknown = normalizeEntry({
      ...previous,
      operationId,
      receipt,
      userId,
      chatId,
      callId,
    });
    if (!sameIdentity(previous, unknown)) {
      throw journalError(
        'locker_office_journal_conflict',
        'Secure Office operation identity changed.'
      );
    }
    if (STATUS_INDEX[previous.status] < STATUS_INDEX.running) {
      throw journalError(
        'locker_office_journal_resolution_required',
        'A pre-running Office operation is abandoned, not cleared.'
      );
    }
    this._removeEntry(oid);
    return true;
  }

  /**
   * The user's escape hatch for an entry the server never backed. It clears
   * only the local record, so it is refused for an operation the server has
   * confirmed pending: that one must go through the server's resolve path,
   * which is gated on the owner lease and a grace period.
   */
  dismissUnbacked({
    operationId,
    receipt,
    userId,
    chatId,
    callId,
    confirmDismissed,
  } = {}) {
    if (confirmDismissed !== true) {
      throw journalError(
        'locker_office_journal_resolution_required',
        'Dismissal confirmation is required for this Office operation.'
      );
    }
    const oid = requireOperationId(operationId);
    const previous = this._readEntry(oid);
    if (!previous) return false;
    const dismissed = normalizeEntry({
      ...previous,
      operationId,
      receipt,
      userId,
      chatId,
      callId,
    });
    if (!sameIdentity(previous, dismissed)) {
      throw journalError(
        'locker_office_journal_conflict',
        'Secure Office operation identity changed.'
      );
    }
    if (previous.serverConfirmedPending === true) {
      throw journalError(
        'locker_office_journal_resolution_required',
        'The server reports this Office operation as still pending.'
      );
    }
    this._removeEntry(oid);
    return true;
  }

  /**
   * Retire entries the server never confirmed as pending once they are past
   * both ceilings. Without this a journal entry outlives every server record
   * of it and the pane carries a banner it can never clear.
   */
  pruneUnbacked({
    userId = null,
    ceiling = RECONCILE_ATTEMPT_CEILING,
    maxAgeMs = UNBACKED_ENTRY_MAX_AGE_MS,
  } = {}) {
    const nowMs = new Date(this.now()).getTime();
    const removed = [];
    for (const entry of this.list({ userId })) {
      if (entry.serverConfirmedPending === true) continue;
      if (!isReconcileExhausted(entry, ceiling)) continue;
      const ageMs = nowMs - Date.parse(lastLifecycleAt(entry));
      if (!Number.isFinite(ageMs) || ageMs < maxAgeMs) continue;
      this._removeEntry(entry.operationId);
      removed.push(entry);
    }
    return Object.freeze(removed);
  }

  classify(operationId) {
    const entry = this._readEntry(operationId);
    if (!entry) {
      throw journalError(
        'locker_office_journal_missing',
        'Secure Office operation recovery data is missing.'
      );
    }
    return RECOVERY_BY_STATUS[entry.status];
  }

  list({ userId = null, status = null } = {}) {
    const uid = userId === null
      ? null
      : requireIdentifier(userId, 'userId');
    if (
      status !== null
      && !JOURNAL_STATUSES.includes(status)
    ) {
      throw journalError(
        'locker_office_journal_invalid',
        'Invalid Office operation status.'
      );
    }
    return this._readAll()
      .filter((entry) => uid === null || entry.userId === uid)
      .filter((entry) => status === null || entry.status === status)
      .sort((left, right) =>
        left.preparedAt.localeCompare(right.preparedAt)
      )
      .map(cloneEntry);
  }

  recoveryPlan({ userId = null } = {}) {
    const plan = {
      safeToAbandon: [],
      requiresExplicitResolution: [],
      pollExactCompletion: [],
    };
    for (const entry of this.list({ userId })) {
      const classification = RECOVERY_BY_STATUS[entry.status];
      if (
        classification
        === RECOVERY_CLASSIFICATIONS.SAFE_TO_ABANDON
      ) {
        plan.safeToAbandon.push(entry);
      } else if (
        classification
        === RECOVERY_CLASSIFICATIONS.REQUIRES_EXPLICIT_RESOLUTION
      ) {
        plan.requiresExplicitResolution.push(entry);
      } else {
        plan.pollExactCompletion.push(entry);
      }
    }
    return Object.freeze({
      safeToAbandon: Object.freeze(plan.safeToAbandon),
      requiresExplicitResolution: Object.freeze(
        plan.requiresExplicitResolution
      ),
      pollExactCompletion: Object.freeze(
        plan.pollExactCompletion
      ),
    });
  }

  async flushCompleted({
    userId,
    confirmComplete,
    ceiling = RECONCILE_ATTEMPT_CEILING,
  }) {
    if (typeof confirmComplete !== 'function') {
      throw journalError(
        'locker_office_journal_invalid',
        'Secure Office operation completion check is unavailable.'
      );
    }
    const entries = this.list({ userId }).filter(
      (entry) =>
        RECOVERY_BY_STATUS[entry.status]
        === RECOVERY_CLASSIFICATIONS.POLL_EXACT_COMPLETION
    );
    const results = [];
    for (const entry of entries) {
      // The ceiling is checked before the call, not after, so an exhausted
      // entry costs the server no further requests at all.
      if (isReconcileExhausted(entry, ceiling)) {
        results.push(Object.freeze({
          operationId: entry.operationId,
          completed: false,
          outcome: RECONCILE_OUTCOMES.UNRECONCILED,
          exhausted: true,
        }));
        continue;
      }
      const answer = await Promise.resolve(
        confirmComplete(cloneEntry(entry))
      ).catch(() => false);
      const { completed, outcome } = normalizeConfirmation(answer);
      if (completed) {
        const current = this._readEntry(entry.operationId);
        if (
          current
          && RECOVERY_BY_STATUS[current.status]
            === RECOVERY_CLASSIFICATIONS.POLL_EXACT_COMPLETION
          && sameIdentity(current, entry)
        ) {
          this._removeEntry(entry.operationId);
        }
        results.push(Object.freeze({
          operationId: entry.operationId,
          completed: true,
          outcome,
          exhausted: false,
        }));
        continue;
      }
      const updated = this.recordReconcileOutcome(
        entry.operationId,
        outcome
      );
      results.push(Object.freeze({
        operationId: entry.operationId,
        completed: false,
        outcome,
        exhausted: updated
          ? isReconcileExhausted(updated, ceiling)
          : true,
      }));
    }
    return Object.freeze(results);
  }
}

export {
  DEFAULT_STORAGE_KEY,
  JOURNAL_SCHEMA,
  JOURNAL_STATUSES,
  MAX_ENTRIES,
  RECONCILE_ATTEMPT_CEILING,
  RECONCILE_OUTCOMES,
  RECOVERY_CLASSIFICATIONS,
  UNBACKED_ENTRY_MAX_AGE_MS,
};

export default LockerOfficeQuiescenceJournal;
